/**
 * AddMealModal — AI-first bottom sheet for logging health data.
 * Free-form text + photo upload. No forms, no dropdowns — just tell Xavior.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface AddMealModalProps {
  onSubmit: (message: string, photo?: File) => void;
  onClose: () => void;
  submitting?: boolean;
  mode?: 'log' | 'labs' | 'profile';
}


export default function AddMealModal({ onSubmit, onClose, submitting, mode = 'log' }: AddMealModalProps) {
  const isLabs = mode === 'labs';
  const isProfile = mode === 'profile';
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [sheetVisible, setSheetVisible] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setSheetVisible(true));
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => { if (photoPreview) URL.revokeObjectURL(photoPreview); };
  }, [photoPreview]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    autoResize();
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = () => {
    const msg = text.trim();
    if ((!msg && !photo) || submitting) return;
    onSubmit(msg || 'Identify this food from the photo', photo ?? undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClose = () => {
    setSheetVisible(false);
    setTimeout(onClose, 200);
  };

  const canSubmit = (text.trim().length > 0 || photo) && !submitting;

  return createPortal(
    <>
    <style>{`@keyframes pulse-green { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.8); } }`}</style>
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: sheetVisible ? 'rgba(0,0,0,0.35)' : 'transparent',
        transition: 'background 0.2s ease',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 16,
          padding: 20,
          width: 400, maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          opacity: sheetVisible ? 1 : 0,
          transform: sheetVisible ? 'scale(1)' : 'scale(0.95)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}>{isLabs ? 'Upload Lab Report' : isProfile ? 'Add to Profile' : 'Log'}</div>
            {isLabs && <div style={{ fontSize: 12, color: 'var(--color-text-faint)', marginTop: 2 }}>Text or photo</div>}
            {isProfile && <div style={{ fontSize: 12, color: 'var(--color-text-faint)', marginTop: 2 }}>Medications, conditions, allergies, goals</div>}
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', fontSize: 18, padding: '2px 4px' }}
          >&#x2715;</button>
        </div>

        <div style={{
          position: 'relative',
          border: '1px solid #e5e7eb', borderRadius: 12,
          background: '#f9fafb',
        }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={isLabs ? "Paste lab results or describe them..." : isProfile ? "e.g. I take metformin 500mg, allergic to penicillin..." : "I had two eggs and a coffee..."}
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 14px 40px',
              border: 'none', borderRadius: 12,
              background: 'transparent',
              fontSize: 14, color: '#1f2937',
              fontFamily: "'DM Sans', sans-serif",
              resize: 'none', outline: 'none',
              minHeight: 80, maxHeight: 120,
              lineHeight: 1.5,
            }}
          />
          <div style={{
            position: 'absolute', bottom: 8, left: 10, right: 10,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-text-faint)', padding: 4, display: 'flex', alignItems: 'center',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                padding: '6px 16px', borderRadius: 8, border: 'none',
                background: submitting ? '#dcfce7' : canSubmit ? '#0ea5e9' : '#e5e7eb',
                color: submitting ? '#16a34a' : canSubmit ? '#fff' : '#9ca3af',
                fontSize: 13, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
                cursor: canSubmit ? 'pointer' : 'default',
              }}
            >
              {submitting ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#22c55e',
                    animation: 'pulse-green 1.2s ease-in-out infinite',
                  }} />
                  Logging...
                </span>
              ) : 'Log'}
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handlePhotoChange}
        />

        {photoPreview && (
          <div style={{ position: 'relative', display: 'inline-flex', alignSelf: 'flex-start' }}>
            <img src={photoPreview} alt="Meal photo" style={{
              width: 80, height: 80, objectFit: 'cover', borderRadius: 10,
              border: '1px solid #e5e7eb',
            }} />
            <button
              onClick={removePhoto}
              style={{
                position: 'absolute', top: -6, right: -6,
                width: 22, height: 22, borderRadius: '50%',
                background: '#374151', border: 'none', cursor: 'pointer',
                color: '#fff', fontSize: 11, display: 'flex',
                alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            >&#x2715;</button>
          </div>
        )}

        {!photoPreview && (
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '18px 16px', borderRadius: 12,
              border: '2px dashed #d1d5db', background: '#fafbfc',
              cursor: 'pointer', width: '100%',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{isLabs ? 'Upload lab report' : 'Upload a photo'}</span>
            <span style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>{isLabs ? 'AI reads your results and extracts test values' : 'AI identifies your food and estimates calories'}</span>
          </button>
        )}
      </div>
    </div>
    </>,
    document.body,
  );
}
